import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, dimensionSummary } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Package } from "@phosphor-icons/react";

// One catalog fetch shared by every picker, refreshed after a minute.
// shortcut: client-side filter over the first 500 active items, move back to
// server search if the catalog outgrows that.
let catalog = { at: 0, promise: null };
const getCatalog = () => {
  if (!catalog.promise || Date.now() - catalog.at > 60000) {
    catalog = {
      at: Date.now(),
      promise: api.get("/items/search?limit=500").then((r) => r.data || [])
        .catch((e) => { catalog = { at: 0, promise: null }; throw e; }),
    };
  }
  return catalog.promise;
};
const matches = (it, q) =>
  [it.name, it.description, it.code, ...[].concat(it.tags || [])]
    .some((s) => String(s || "").toLowerCase().includes(q));

/**
 * Inline combobox for picking an item from the catalog.
 * The suggestion list is rendered through a portal so it escapes any
 * `overflow:hidden|auto` parent (e.g. a scrollable table wrapper).
 */
export default function ItemPicker({
  value, onChange, onSelect, placeholder, className = "", testId, addedIds,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const popRef = useRef(null);

  // Warm the shared catalog cache so the first keystroke filters instantly.
  useEffect(() => { getCatalog().catch(() => {}); }, []);

  // Close on outside click / scroll / resize
  useEffect(() => {
    const onMouseDown = (e) => {
      if (inputRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollOrResize = (e) => {
      // The capture-phase scroll listener also sees the popover's own
      // scrolling — ignore that, or the list closes as soon as it is scrolled.
      if (e?.type === "scroll" && popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, []);

  // Position the popover under the input, flipping above it when there
  // isn't enough room below. Anchored with `bottom` when flipped (instead
  // of a guessed `top` offset) so the box grows away from the input and
  // can never overlap/hide it, regardless of how many items render.
  useLayoutEffect(() => {
    if (!open || !inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const flipUp = spaceBelow < 200 && r.top > spaceBelow;
    setCoords({
      top: flipUp ? null : r.bottom + 4,
      bottom: flipUp ? window.innerHeight - r.top + 4 : null,
      left: r.left,
      width: r.width,
    });
  }, [open, items.length]);

  // Filter the cached catalog locally - only once the user has typed a character
  useEffect(() => {
    if (!open || !value) { setItems([]); return; }
    let alive = true;
    const q = value.trim().toLowerCase();
    getCatalog()
      .then((all) => { if (alive) { setItems(all.filter((it) => matches(it, q)).slice(0, 20)); setActive(0); } })
      .catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, [value, open]);

  // Already-added items sink to the bottom (stable sort keeps server order).
  const isAdded = (it) => !!addedIds?.has(it.id);
  const shown = [...items].sort((a, b) => isAdded(a) - isAdded(b));

  const choose = (it) => {
    setOpen(false);
    onSelect && onSelect(it);
  };

  const onKey = (e) => {
    if (!open || shown.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(shown[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <>
      <div ref={inputRef}>
        <Input
          data-testid={testId}
          value={value || ""}
          onChange={(e) => { const v = e.target.value; onChange(v); setOpen(v.length >= 1); }}
          onKeyDown={onKey}
          placeholder={placeholder}
          className={className}
          autoComplete="off"
        />
      </div>
      {open && items.length > 0 && createPortal(
        <div
          ref={popRef}
          data-testid="item-suggestions"
          style={{
            position: "fixed",
            top: coords.top ?? undefined,
            bottom: coords.bottom ?? undefined,
            left: coords.left,
            width: Math.max(coords.width, 280),
            zIndex: 1000,
          }}
          className="max-h-72 overflow-auto bg-white border border-zinc-300 rounded-sm shadow-lg"
        >
          {shown.map((it, i) => (
            <button
              type="button"
              key={it.id}
              onClick={() => choose(it)}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 border-b border-zinc-100 last:border-0 ${
                i === active ? "bg-zinc-100" : "hover:bg-zinc-50"
              } ${isAdded(it) ? "opacity-50" : ""}`}
              data-testid={`item-suggestion-${it.code}`}
            >
              <Package size={14} className="mt-0.5 text-zinc-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {it.name}
                  {dimensionSummary(it) && (
                    <span className="text-zinc-500 font-normal"> · {dimensionSummary(it)}</span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 font-mono truncate">{it.code}</div>
              </div>
              {isAdded(it) && (
                <span className="text-[11px] text-emerald-700 font-medium shrink-0" data-testid="item-added-badge">✓ Added</span>
              )}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
